'use client';
import React, { useState } from 'react';
import Image from 'next/image';

interface TTOSRobotMascotProps {
  className?: string;
  size?: number;
  glow?: boolean;
}

export const TTOSRobotMascot: React.FC<TTOSRobotMascotProps> = ({
  className = '',
  size = 180,
  glow = true,
}) => {
  const [imgError, setImgError] = useState(false);

  return (
    <div
      className={`relative flex items-center justify-center select-none ${className}`}
      style={{ width: size, height: size }}
    >
      {/* Ambient Glow */}
      {glow && (
        <div
          className="absolute inset-0 rounded-full blur-2xl opacity-60 bg-gradient-to-tr from-cyan-400/40 via-indigo-500/30 to-purple-500/40 animate-pulse pointer-events-none"
          style={{ transform: 'scale(1.15)' }}
        />
      )}

      {!imgError ? (
        <div className="relative w-full h-full drop-shadow-[0_20px_35px_rgba(99,102,241,0.35)] transition-transform duration-500 hover:scale-105">
          <Image
            src="/images/ttos-robot-mascot.jpg"
            alt="TT OS AI Robot Assistant"
            width={size}
            height={size}
            className="w-full h-full object-contain rounded-2xl"
            onError={() => setImgError(true)}
            priority
          />
        </div>
      ) : (
        /* Futuristic 3D Vector Fallback */
        <div
          className="relative w-full h-full flex flex-col items-center justify-center transition-transform duration-300 hover:scale-105"
          style={{ width: size, height: size }}
        >
          <svg viewBox="0 0 160 160" className="w-full h-full drop-shadow-xl" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="bodyGrad" x1="20" y1="20" x2="140" y2="140" gradientUnits="userSpaceOnUse">
                <stop stopColor="#FFFFFF" />
                <stop offset="0.6" stopColor="#F1F5F9" />
                <stop offset="1" stopColor="#E2E8F0" />
              </linearGradient>
              <linearGradient id="visorGrad" x1="40" y1="45" x2="120" y2="85" gradientUnits="userSpaceOnUse">
                <stop stopColor="#0B0F19" />
                <stop offset="1" stopColor="#1E293B" />
              </linearGradient>
              <filter id="neonCyan" x="0" y="0" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>

            {/* Floating Thruster Glow */}
            <ellipse cx="80" cy="148" rx="20" ry="5" fill="#38BDF8" opacity="0.4" className="animate-pulse" />

            {/* Robot Head Outer Frame */}
            <rect x="36" y="28" width="88" height="68" rx="34" fill="url(#bodyGrad)" stroke="#CBD5E1" strokeWidth="2.5" />
            
            {/* Top Antenna */}
            <circle cx="80" cy="14" r="5" fill="#38BDF8" filter="url(#neonCyan)" />
            <line x1="80" y1="18" x2="80" y2="28" stroke="#94A3B8" strokeWidth="3" strokeLinecap="round" />

            {/* Dark Visor */}
            <rect x="46" y="40" width="68" height="42" rx="20" fill="url(#visorGrad)" stroke="#334155" strokeWidth="1.5" />

            {/* Glowing Cyan Eyes */}
            <ellipse cx="64" cy="61" rx="8" ry="9" fill="#06B6D4" filter="url(#neonCyan)" />
            <ellipse cx="96" cy="61" rx="8" ry="9" fill="#06B6D4" filter="url(#neonCyan)" />
            <circle cx="66" cy="58" r="2.5" fill="#FFFFFF" opacity="0.9" />
            <circle cx="98" cy="58" r="2.5" fill="#FFFFFF" opacity="0.9" />

            {/* Ears / Headset */}
            <rect x="29" y="47" width="8" height="24" rx="4" fill="#64748B" />
            <rect x="123" y="47" width="8" height="24" rx="4" fill="#64748B" />

            {/* Torso */}
            <path d="M52 100 C52 94 60 90 80 90 C100 90 108 94 108 100 L114 134 C114 142 102 144 80 144 C58 144 46 142 46 134 Z" fill="url(#bodyGrad)" stroke="#CBD5E1" strokeWidth="2" />
            
            {/* Chest Core Indicator */}
            <circle cx="80" cy="116" r="8" fill="#38BDF8" filter="url(#neonCyan)" />
            <circle cx="80" cy="116" r="4" fill="#FFFFFF" />

            {/* Waving Hand */}
            <g className="origin-[125px_110px] animate-bounce">
              <path d="M112 104 C116 100 126 94 132 88 C136 84 140 88 138 93 L130 108" stroke="url(#bodyGrad)" strokeWidth="10" strokeLinecap="round" />
              <circle cx="137" cy="87" r="4" fill="#F8FAFC" stroke="#94A3B8" />
            </g>
          </svg>
        </div>
      )}
    </div>
  );
};

export default TTOSRobotMascot;
